// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppEvent, NavItem } from '@interfaces/interfaces';
import { s3Asset } from '@interfaces/s3asset.interface';
import { Observable, from, takeWhile } from 'rxjs';
import { DownloadModalComponent } from 'src/app/modals/download-modal/download-modal.component';
import { NavService } from '@services/nav.service';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '@services/user.service';
import { DealershipService } from '@services/dealership.service';
import { UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { PermissionsService } from '@services/permissions.service';
import { ErrorModalComponent } from 'src/app/modals/error-modal/error-modal.component';
import { Dealer, DealerProducts } from '@interfaces/dealer.interfaces';
import { SettingsService } from '@services/settings.service';
import { ContentService } from '@services/content.service';
import { getEnrollmentStatus } from 'src/app/utils/utils';

@Component({
    selector: 'dealer-side-nav',
    templateUrl: './dealer-side-nav.component.html',
    styleUrls: ['./dealer-side-nav.component.scss'],
})
export class DealerSideNavComponent implements OnInit, OnDestroy {
    private isComponentAlive = true;
    public menu$: Observable<NavItem[]>;
    private dealer: Dealer;
    private s3Assets: s3Asset[];
    public hasPrintToDdjEnrollment = false;
    public hasPrintToDdjPermission = false;
    public isInternalUser = false;
    public isCollapsed = undefined;

    protected supportPhone = '';
    private supportEmail = '';

    constructor(
        private readonly dialog: MatDialog,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly dealerService: DealershipService,
        private readonly route: ActivatedRoute,
        private readonly permissionService: PermissionsService,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService,
        private readonly contentService: ContentService
    ) {
        this.userService
            .user$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                this.isInternalUser = user.userType === UserTypes.Internal;
                if (user?.dealerAppPermissions?.length > 0) {
                    this.hasPrintToDdjPermission = Boolean(user.dealerAppPermissions[0]?.userPermissionId.find((x) => x === UserPermissionIds.PrintToDDJ));
                } else {
                    this.hasPrintToDdjPermission = false;
                }
                this.menu$ = this.navService.getSideNavMenu();
            });

        this.dealerService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((dealer) => {
            navService.setLeadsNavItemTitle(dealer.oemCode);
            if (dealer) {
                this.dealer = dealer;
            }
        });
    }

    ngOnInit(): void {
        this.s3Assets = this.route.snapshot.data.pddj;

        this.navService.navState$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((state) => {
            this.isCollapsed = state === 'collapsed';
        });

        from(this.settingsService.getOemSettings(this.dealer.oemCode))
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((settings) => {
                this.supportEmail = settings.supportEmail;
                this.supportPhone = settings.supportPhone;
            });

        this.dealershipService
            .getDealerEnrollment$(this.dealer.dealerId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((dealerEnrollment) => {
                for (const enrollment of dealerEnrollment) {
                    if (DealerProducts.EzDocsAutomate === enrollment.productId) {
                        this.hasPrintToDdjEnrollment = enrollment.isActive;
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    download() {
        if ((this.hasPrintToDdjEnrollment && this.hasPrintToDdjPermission) || this.isInternalUser) {
            this.dialog.open(DownloadModalComponent, {
                width: '700px',
                closeOnNavigation: false,
                data: { s3assetId: this.s3Assets[0].s3AssetId },
            });
        }
    }

    toggleNavState(): void {
        const newState = this.isCollapsed ? 'expanded' : 'collapsed';
        this.navService.setNavState(newState);
    }

    getButtonClass(): string {
        return (this.hasPrintToDdjEnrollment && this.hasPrintToDdjPermission) || this.isInternalUser ? 'download-pddj' : 'download-pddj window-shop';
    }

    public async eventHandler(event: AppEvent): Promise<void> {
        if ('navigate' !== event.context) {
            return;
        }

        const menuItem: NavItem = event.data;
        if (this.permissionService.hasPermission(menuItem.permissions?.permissionIds)) {
            this.navService.setSelectedRoute('dashboard', menuItem);
        } else {
            const errorMessage = await this.contentService.getAccessRestrictedContent(
                this.dealer.dealerId,
                this.dealer.oemCode,
                menuItem.permissions?.permissionIds
            );

            errorMessage.pipe(takeWhile(() => this.isComponentAlive)).subscribe((message) => {
                this.dialog.open(ErrorModalComponent, {
                    width: '700px',
                    data: {
                        title: 'Access Restricted',
                        message,
                    },
                });
            });
        }
    }
}
